import React, { useEffect, useRef } from "react";
import Styles from "./selectDateSection.module.scss";
import { DAYS } from "@src/utils/date";
import useOnScreen from "@src/utils/hooks/useOnScreen";
import { useLocation } from "react-router-dom";

export function getCalendarDates(period = 24) {
  const calendarDates = [];
  const currentDate = new Date();

  for (let i = 0; i < period; i++) {
    // 현재 월에서 i만큼 더한 연도와 월 계산
    const year = currentDate.getFullYear() + Math.floor((currentDate.getMonth() + i) / 12);
    const month = (currentDate.getMonth() + i) % 12;
    // 날짜 배열 생성
    const dates = [];

    // 해당 월의 첫 번째 날
    const firstDay = new Date(year, month, 1);

    const dayOfWeek = firstDay.getDay(); // 0: 일요일, 1: 월요일, ..., 6: 토요일

    for (let j = 0; j < dayOfWeek; j++) {
      dates.push(""); // 첫 번째 날 이전은 빈 문자열
    }
    // 해당 월의 마지막 날
    const totalDaysInMonth = new Date(year, month + 1, 0).getDate(); // 현재 월의 마지막 날 계산

    for (let day = 1; day <= totalDaysInMonth; day++) {
      dates.push(`${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`);
    }

    // calendarDates에 추가
    calendarDates.push({
      yearAndMonth: `${year}-${String(month + 1).padStart(2, "0")}`,
      dates: dates,
    });
  }

  return calendarDates;
}

const CALENDAR_DATES: { dates: string[]; yearAndMonth: string }[] = getCalendarDates();
const DAY_OF_WEEKS = DAYS;
const EL_DAY_TAB_HEIGHT = 64; // 월, 상세 선택 탭 높이

type Props = {
  handleComplete: () => void;
  handleReset: () => void;
  selectedDate: { startDate: string; endDate: string };
  setSelectedDate: (value: React.SetStateAction<{ startDate: string; endDate: string }>) => void;
  selectedMonths: string[];
};

export default function SelectDateSection({
  handleComplete,
  handleReset,
  selectedDate,
  setSelectedDate,
  selectedMonths,
}: Props) {
  const sectionWrapRef = useRef<HTMLDivElement>(null);
  const isVisible = useOnScreen(sectionWrapRef);
  const location = useLocation();
  const isProductTab = location.search.includes("tabId=2");

  const deleteZero = (date: string) => {
    if (Number(date) < 10) {
      return date.substring(1, 2);
    }
    return date;
  };
  const formatDate = (date: string) => {
    const fDate = date.substring(8, 10);
    return deleteZero(fDate);
  };
  const handleDateClick = (date: string) => {
    if (!selectedDate.startDate) {
      return setSelectedDate({ ...selectedDate, startDate: date });
    }
    // 시작일이 선택되어있고, 선택한 날짜가 시작일보다 이전이면 시작일 변경
    if (
      selectedDate.startDate &&
      Number(selectedDate.startDate.replaceAll("-", "")) > Number(date.replaceAll("-", ""))
    ) {
      return setSelectedDate({ startDate: date, endDate: "" });
    }
    if (selectedDate.startDate && !selectedDate.endDate) {
      return setSelectedDate({ ...selectedDate, endDate: date });
    }
    // 시작일과 종료일이 선택되어있고, 새로운 날짜를 선택하면 시작일 변경
    if (selectedDate.startDate && selectedDate.endDate) {
      return setSelectedDate({ startDate: date, endDate: "" });
    }
  };
  const isIncludedBetweenSelectedDate = (date: string) => {
    if (date === "") return false;
    // 범위 설정
    const startDate = new Date(selectedDate.startDate);
    const endDate = new Date(selectedDate.endDate);

    // 선택한 날짜를 Date 객체로 변환
    const selectedDateObj = new Date(date);

    // 날짜 비교
    if (selectedDateObj >= startDate && selectedDateObj <= endDate) {
      return true;
    } else {
      return false;
    }
  };

  useEffect(() => {
    // 해당 컴포넌트가 화면에 보일때 날짜가 선택되어있다면 선택된 날짜로 스크롤 시킴
    if (isVisible) {
      if (sectionWrapRef.current) {
        const sectionWrapElement: HTMLElement = sectionWrapRef.current;
        const titleElement: HTMLElement = sectionWrapRef.current.querySelector(
          `.${Styles["start-date-tile"]}`,
        );
        if (titleElement) {
          sectionWrapRef.current.scrollTop =
            titleElement.offsetTop - sectionWrapElement.offsetTop - EL_DAY_TAB_HEIGHT;
        }
      }
    }
  }, [isVisible]);

  return (
    <>
      <section className={Styles["section-container"]} ref={sectionWrapRef}>
        <div className={Styles["section-wrap"]}>
          <div className={Styles["day-of-week-wrap"]}>
            {DAY_OF_WEEKS.map((day) => (
              <p key={day}>{day}</p>
            ))}
          </div>
          {CALENDAR_DATES.map(({ yearAndMonth, dates }) => (
            <article key={yearAndMonth} className={Styles["calendar-wrap"]}>
              <p
                className={Styles["year-and-month"]}
              >{`${yearAndMonth.split("-")[0]}년 ${deleteZero(yearAndMonth.split("-")[1])}월`}</p>
              <div className={Styles["date-wrap"]}>
                {dates.map((date, i) => (
                  <div
                    key={i}
                    className={`
                  ${Styles["tile-wrap"]}
                  ${selectedDate.startDate !== selectedDate.endDate && selectedDate.startDate !== "" && selectedDate.endDate !== "" && date !== "" && selectedDate.startDate === date ? Styles["start-date-tile"] : ""}
                  ${selectedDate.startDate !== selectedDate.endDate && selectedDate.startDate !== "" && selectedDate.endDate !== "" && date !== "" && selectedDate.endDate === date ? Styles["end-date-tile"] : ""}
                  ${selectedDate.startDate !== selectedDate.endDate && isIncludedBetweenSelectedDate(date) ? Styles["selected-date-tile"] : ""}`}
                  >
                    <div
                      className={
                        (date !== "" && selectedDate.startDate === date) ||
                        (date !== "" && selectedDate.endDate === date)
                          ? Styles["selected-date"]
                          : ""
                      }
                      onClick={() => handleDateClick(date)}
                    >
                      <p>{formatDate(date)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </article>
          ))}
        </div>
        <div className={Styles["fake-box"]}></div>
      </section>
      <div className={Styles["btn-wrap"]}>
        <button
          className={Styles["reset-btn"]}
          onClick={handleReset}
          disabled={selectedMonths.length === 0 && selectedDate.startDate === ""}
        >
          초기화
        </button>
        <button className={Styles["complete-btn"]} onClick={() => handleComplete()}>
          {isProductTab && "적용"}
          {!isProductTab &&
            (selectedMonths.length === 0 && selectedDate.startDate === "" ? "건너뛰기" : "다음")}
        </button>
      </div>
    </>
  );
}
